/**
 * @file schemas.ts
 * @brief Zod schemas for Medical Assessment + Case Hub API responses.
 *
 * @author DES
 * @date 02-09-2026
 */

import { z } from "zod";

export const CaseHubClaimantSchema = z
  .object({
    claimantId: z.union([z.string(), z.number()]).nullable().optional(),
    claimantKey: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    forenames: z.string().nullable().optional(),
    surname: z.string().nullable().optional(),
    sex: z.string().nullable().optional(),
    dateOfBirth: z.string().nullable().optional(),
    address: z.string().nullable().optional(),
    postCode: z.string().nullable().optional(),
    mobile: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    accidentDate: z.string().nullable().optional(),
    accidentType: z.string().nullable().optional(),
    proofOfIdentity: z.string().nullable().optional(),
    dominantHand: z.string().nullable().optional(),
  })
  .passthrough();

export const CaseHubRefsSchema = z
  .object({
    uniqueReferenceNumber: z.string().nullable().optional(),
    agencyReferenceNo: z.string().nullable().optional(),
    partyReferenceNo: z.string().nullable().optional(),
  })
  .passthrough();

export const CaseHubScheduleSchema = z
  .object({
    clinicName: z.string().nullable().optional(),
    clinicAddress: z.string().nullable().optional(),
    appointmentDate: z.string().nullable().optional(),
    appointmentTime: z.string().nullable().optional(),
    examinationDurationMinutes: z.number().nullable().optional(),
    accompaniedBy: z.string().nullable().optional(),
  })
  .passthrough()
  .nullable()
  .optional();

export const CaseHubSchema = z
  .object({
    source: z.string().optional(),
    pulledAt: z.string().optional(),
    claimant: CaseHubClaimantSchema.optional(),
    refs: CaseHubRefsSchema.optional(),
    doctor: z.record(z.unknown()).optional(),
    schedule: CaseHubScheduleSchema,
    identitySheet: z.record(z.string()).optional(),
    medyx: z.record(z.unknown()).optional(),
  })
  .passthrough();

export type CaseHub = z.infer<typeof CaseHubSchema>;

export const MedicalAssessmentSchema = z
  .object({
    id: z.string(),
    ownerId: z.string().nullable().optional(),
    companyId: z.string().nullable().optional(),
    sessionId: z.string().nullable().optional(),
    title: z.string(),
    status: z.string(),
    caseHub: CaseHubSchema.optional().default({}),
    metadata: z.record(z.unknown()).optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .passthrough();

export type MedicalAssessment = z.infer<typeof MedicalAssessmentSchema>;

export const AssessmentStatementSchema = z
  .object({
    id: z.string(),
    assessmentId: z.string(),
    section: z.string(),
    xmlTag: z.string(),
    details: z.string(),
    facts: z.record(z.unknown()).optional(),
    sortOrder: z.number().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .passthrough();

export type AssessmentStatement = z.infer<typeof AssessmentStatementSchema>;

export const AssessmentsListResponseSchema = z.object({
  success: z.literal(true),
  assessments: z.array(MedicalAssessmentSchema),
});

export const AssessmentDetailResponseSchema = z.object({
  success: z.literal(true),
  assessment: MedicalAssessmentSchema,
  statements: z.array(AssessmentStatementSchema).optional().default([]),
});

export const CreateAssessmentResponseSchema = z.object({
  success: z.literal(true),
  assessment: MedicalAssessmentSchema,
});

export const CaseHubPullResponseSchema = z.object({
  success: z.literal(true),
  assessment: MedicalAssessmentSchema,
});

export const AddStatementResponseSchema = z.object({
  success: z.literal(true),
  statement: AssessmentStatementSchema,
});

export const ExaminationSchema = z
  .object({
    id: z.string(),
    assessmentId: z.string(),
    generalFindings: z.string().nullable().optional(),
    presentationFindings: z.string().nullable().optional(),
    findingsByInjury: z.record(z.string()).optional().default({}),
    recordedBy: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .passthrough();

export type Examination = z.infer<typeof ExaminationSchema>;

export const MedicalRecordsEntrySchema = z.object({
  phase: z.enum(["pre", "post"]),
  source: z.string(),
  dateLabel: z.string().optional(),
  narrative: z.string(),
  footnote: z.string().optional(),
});

export type MedicalRecordsEntry = z.infer<typeof MedicalRecordsEntrySchema>;

export const RegistrationDetailsSchema = z.object({
  gmcNumber: z.string().optional(),
  mpsNumber: z.string().optional(),
  icoReference: z.string().optional(),
  medcoId: z.string().optional(),
  medcoTrainingDate: z.string().optional(),
  currentPositions: z.string().optional(),
  experience: z.string().optional(),
});

export const CaseClassificationSchema = z.object({
  motorVehicleOccupant: z.string().optional(),
  softTissueInjuryClaim: z.string().optional(),
  firstReportInRelationToIncident: z.string().optional(),
});

export const MlrSupplementsSchema = z
  .object({
    id: z.string(),
    assessmentId: z.string(),
    medicalRecordsReview: z.string().nullable().optional(),
    medicalRecordsEntries: z.array(MedicalRecordsEntrySchema).optional(),
    medicalRecordsSummary: z.string().nullable().optional(),
    prognosisOpinion: z.string().nullable().optional(),
    registrationDetails: RegistrationDetailsSchema.optional(),
    caseClassification: CaseClassificationSchema.optional(),
    specialConsiderations: z.string().nullable().optional(),
    futureTreatment: z.string().nullable().optional(),
    qualifications: z.string().nullable().optional(),
    /* §4 closing — the physician's view of the claimant's account (16-09-2026).
       `null` means NOT RECORDED and must never be rendered as agreement. */
    accountAgreement: z.enum(["agrees", "partly", "disagrees"]).nullable().optional(),
    accountAssessment: z.string().nullable().optional(),
    injuriesDenied: z
      .array(
        z.object({
          injury: z.string(),
          source: z.enum(["clinic_list", "instruction"]).optional(),
          note: z.string().nullable().optional(),
        }),
      )
      .optional(),
    exceptionalCircumstances: z.string().nullable().optional(),
    /*
     * §9's three answers. Separate fields because the report answers them
     * independently — a real incumbent report has whiplash No, circumstances
     * Yes, agreement No, which a single derived flag could not express.
     * ⚠️ `null` is unanswered and prints a blank rule, never "No".
     */
    exceptionalWhiplashReported: z.string().nullable().optional(),
    exceptionalCircumstancesReported: z.string().nullable().optional(),
    exceptionalSeverityAgreed: z.string().nullable().optional(),
    /** §1 — the doctor checked the claimant against the ID they showed. */
    photoIdMatched: z.boolean().nullable().optional(),
    statementOfTruthConfirmed: z.boolean().optional(),
    statementOfTruthSignedAt: z.string().nullable().optional(),
    statementOfTruthSignerName: z.string().nullable().optional(),
    statementOfTruthSignedBy: z.string().nullable().optional(),
    statementOfTruthAudit: z.array(z.record(z.unknown())).optional(),
    recordedBy: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .passthrough();

export const PrognosisSchema = z
  .object({
    id: z.string(),
    assessmentId: z.string(),
    statementId: z.string().nullable().optional(),
    injuryKey: z.string(),
    injuryLabel: z.string(),
    symptomSummary: z.string().nullable().optional(),
    examSnippet: z.string().nullable().optional(),
    aiDraftText: z.string().nullable().optional(),
    confirmedText: z.string().nullable().optional(),
    status: z.string(),
    source: z.string().nullable().optional(),
    structuredFields: z.record(z.unknown()).optional(),
    attestedBy: z.string().nullable().optional(),
    attestedAt: z.string().nullable().optional(),
    auditTrail: z.array(z.record(z.unknown())).optional(),
    /**
     * Why the injury was classified the way it was. Present only when a RULE
     * decided it — never when the doctor stated the classification themselves,
     * because attaching a regulation to their own sentence would dress an
     * opinion up as a derivation.
     */
    classification: z
      .object({
        value: z.string(),
        ruleId: z.string(),
        cite: z.string(),
        because: z.array(
          z.object({ path: z.string(), actual: z.unknown() }),
        ),
      })
      .nullable()
      .optional(),
    /**
     * The one thing the rulebook could not work out from the recording.
     *
     * ⚠️ Not a form field. Present only while the recording leaves it open AND
     * nobody has answered — a classification picker that is always on screen is
     * the desktop tool this module replaces.
     */
    openQuestion: z
      .object({
        decision: z.string(),
        /** A scale gets an input; a choice gets buttons. */
        kind: z.enum(["enum", "number"]).optional(),
        question: z.string(),
        hint: z.string().optional(),
        min: z.number().nullable().optional(),
        max: z.number().nullable().optional(),
        options: z.array(z.object({ value: z.string(), label: z.string() })),
      })
      .nullable()
      .optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .passthrough();

export type Prognosis = z.infer<typeof PrognosisSchema>;

export const ExaminationResponseSchema = z.object({
  success: z.literal(true),
  examination: ExaminationSchema.nullable(),
});

export const MlrSupplementsResponseSchema = z.object({
  success: z.literal(true),
  supplements: MlrSupplementsSchema.nullable(),
  ingest: z
    .object({
      method: z.enum(["pdf-parse", "ocr"]),
    })
    .optional(),
});

export const PrognosesListResponseSchema = z.object({
  success: z.literal(true),
  prognoses: z.array(PrognosisSchema),
});

export const PrognosisConfirmResponseSchema = z.object({
  success: z.literal(true),
  prognosis: PrognosisSchema,
});

export const PrognosisVoiceTranscribeResponseSchema = z.object({
  success: z.literal(true),
  text: z.string(),
  language: z.string().nullable().optional(),
  heard: z.boolean(),
});

export const DoctorLinkSchema = z
  .object({
    userId: z.string(),
    email: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    doctorId: z.number().nullable().optional(),
    doctorKey: z.string().nullable().optional(),
  })
  .passthrough();

export type DoctorLink = z.infer<typeof DoctorLinkSchema>;

export const MedyxDoctorSchema = z
  .object({
    doctorId: z.number().nullable().optional(),
    doctorKey: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    qualifications: z.string().nullable().optional(),
  })
  .passthrough();

export const DoctorLinkResponseSchema = z.object({
  success: z.literal(true),
  doctorLink: DoctorLinkSchema.nullable(),
});

export const DoctorLinkLookupResponseSchema = z.object({
  success: z.literal(true),
  doctor: MedyxDoctorSchema,
  userLink: DoctorLinkSchema.nullable().optional(),
});

export const IntakeInboxResponseSchema = z.object({
  success: z.literal(true),
  inbox: z.array(MedicalAssessmentSchema).default([]),
});

export const AcceptIntakeResponseSchema = z.object({
  success: z.literal(true),
  assessment: MedicalAssessmentSchema,
});

export const AutoGenerateResponseSchema = z.object({
  success: z.literal(true),
  assessmentId: z.string(),
  sections: z.number(),
  statementsSaved: z.number(),
  prognosesSeeded: z.number(),
  transcriptLength: z.number(),
});

// ----------------------------------------------------------------------
// Report sections — the assembled 13-section MLR, served as data.
//
// The shape mirrors BACKEND `export/mlrSectionModel.mjs`, which is the single
// source the PDF is drawn from. The panel renders what arrives and decides
// nothing about what belongs in a section, so the document on screen and the
// exported PDF cannot say different things. Replaces the hand-kept
// `mlrDocumentSections.ts` / `mlrEditorCopy.ts` copies. (13-09-2026)
//
// Validation is deliberately shallow: blocks are carried through with
// `passthrough()` and typed in TypeScript rather than parsed field by field.
// A strict union that rejected one unfamiliar block would blank the whole
// report — the wrong trade for a document a doctor is mid-edit on.
// ----------------------------------------------------------------------

export const MlrSectionStatusSchema = z.enum([
  "complete",
  "empty",
  "needsDoctor",
]);

export const MlrReportSectionSchema = z
  .object({
    number: z.number(),
    id: z.string(),
    title: z.string(),
    status: MlrSectionStatusSchema,
    blocks: z.array(z.object({ type: z.string() }).passthrough()),
    editorSlots: z
      .array(z.object({ kind: z.string() }).passthrough())
      .optional(),
  })
  .passthrough();

/**
 * What the consultation did not cover.
 *
 * A form is a checklist — every box is at least seen, even when the answer is
 * "no". A conversation has no such guarantee, and a report printed without a
 * question having been asked looks identical to one where the answer was no.
 */
export const CoverageSchema = z.object({
  sections: z.array(
    z.object({
      section: z.string(),
      /** Nothing at all recorded for this section — a different warning. */
      absent: z.boolean(),
      applicable: z.number(),
      answered: z.number(),
      missing: z.array(
        z.object({
          key: z.string(),
          question: z.string(),
          /** The doctor asked it; extraction lost it. Not their gap. */
          asked: z.boolean().optional(),
        }),
      ),
      /** The narrowed list — what the doctor still owes. */
      notAsked: z
        .array(z.object({ key: z.string(), question: z.string() }))
        .optional(),
      askedNotCaptured: z
        .array(z.object({ key: z.string(), question: z.string() }))
        .optional(),
    }),
  ),
  applicable: z.number(),
  answered: z.number(),
  missingCount: z.number(),
  complete: z.boolean(),
  /*
   * ⚠️ `missingCount` has always meant "not extracted", and the callout has
   * always called it "not asked". On a real consultation that was 39 questions
   * when 32 had been asked and answered. Optional so an older server still
   * renders; the callout falls back to `missingCount`.
   */
  notAsked: z.number().optional(),
  askedNotCaptured: z.number().optional(),
});

/** Something wrong with the claimant's own record, read before signing. */
export const ClaimantWarningSchema = z.object({
  code: z.string(),
  message: z.string(),
  fields: z.array(z.string()),
});

export type MlrClaimantWarning = z.infer<typeof ClaimantWarningSchema>;

export type MlrCoverage = z.infer<typeof CoverageSchema>;

export const CoverageResponseSchema = z
  .object({
    success: z.boolean().optional(),
    coverage: CoverageSchema,
    /* Ride with coverage because they are read at the same moment. */
    warnings: z.array(ClaimantWarningSchema).optional(),
  })
  .passthrough();

export const ReportSnapshotSchema = z
  .object({
    version: z.number(),
    signerName: z.string().nullable().optional(),
    signedAt: z.union([z.string(), z.date()]).nullable().optional(),
  })
  .passthrough();

export const ReportLifecycleSchema = z.object({
  state: z.enum(["unsigned", "signed", "signed_legacy"]),
  snapshotVersion: z.number().nullable(),
  signedAt: z.union([z.string(), z.date()]).nullable(),
  signerName: z.string().nullable(),
});

export type MlrReportLifecycle = z.infer<typeof ReportLifecycleSchema>;

export const ReportLifecycleResponseSchema = z
  .object({ success: z.boolean().optional(), lifecycle: ReportLifecycleSchema })
  .passthrough();

export const ReportSectionsResponseSchema = z
  .object({
    success: z.boolean().optional(),
    assessmentId: z.string().nullable().optional(),
    claimantName: z.string().nullable().optional(),
    doctorName: z.string().nullable().optional(),
    qualifications: z.string().nullable().optional(),
    reportDate: z.union([z.string(), z.date()]).nullable().optional(),
    statementOfTruthSigned: z.boolean().optional(),
    confirmedPrognosisCount: z.number().optional(),
    /**
     * Present once the report is signed AND frozen (16-09-2026). The sections
     * are then the snapshot taken at signing, with every `editable` target
     * already stripped server-side — so the document renders read-only without
     * this page deciding anything.
     *
     * Absent covers two cases: unsigned, or signed before snapshots existed.
     * `ReportLifecycleSchema` separates them.
     */
    snapshot: ReportSnapshotSchema.nullable().optional(),
    sections: z.array(MlrReportSectionSchema),
  })
  .passthrough();

export type MlrIdentityRow = {
  /**
   * The CANONICAL label, and the key `identitySheet` is stored under on both
   * sides. Address a cell by this, never by `label` — the two differ on report
   * shapes that rename a cell (an exposure report dates a *manifestation*), and
   * keying saved edits by the printed label would orphan them. Optional only
   * for a payload from a backend that predates it. (19-09-2026)
   */
  key?: string;
  /** What this report's shape prints for the cell. */
  label: string;
  value: string;
  /** How the cell is edited — decided server-side, beside the label list. */
  kind: "text" | "multiline" | "date";
};

/**
 * What a block writes back to. `html` is the SOURCE value with markup intact:
 * a block's `items` are stripped for rendering, so opening the editor from
 * those would discard the doctor's formatting on the next save.
 */
export type MlrEditableTarget =
  | { kind: "identity" }
  | { kind: "statement"; id: string; xmlTag: string; html: string }
  | {
      kind: "examination";
      field: "generalFindings" | "presentationFindings";
      html: string;
    }
  | { kind: "examinationInjury"; injuryKey: string; html: string }
  | {
      kind: "supplements";
      field:
        | "medicalRecordsSummary"
        | "prognosisOpinion"
        | "specialConsiderations"
        | "futureTreatment"
        | "qualifications"
        /* §4 closing — the physician's view of the claimant's account. The
           block only appears once something has been recorded, so an editor
           reaching it always has text to work from. (16-09-2026) */
        | "accountAssessment";
      html: string;
    };

/** Which claim the recording does not support — e.g. `causingInjuriesTo: "shoulders"`. */
export type MlrUnverifiedClaim = { field: string; value: string };

export type MlrBlock =
  | { type: "identityTable"; rows: MlrIdentityRow[]; editable?: MlrEditableTarget }
  | { type: "subheading"; text: string }
  | { type: "numberLabel"; text: string }
  | { type: "numberedHeading"; number: string; title: string }
  | {
      type: "paragraphs";
      items: string[];
      nestedListIndent?: boolean;
      editable?: MlrEditableTarget;
      /** Open the editor empty: the text shown is a "not recorded yet" line. */
      placeholder?: boolean;
      /**
       * What this block IS, when a section renders it specially. §9's
       * "exceptionalQuestions" is replaced by Yes/No controls in the panel and
       * printed as prose in the PDF. Named rather than found by position.
       */
      role?: string;
      /**
       * A finding the grounding pass could not tie to anything said in the
       * consultation. Already withheld from the PDF and the GRIP export
       * (BACKEND `factFlags.mjs`); the block is still returned so a person can
       * read it and decide. `value` names the unsupported claim.
       */
      unverified?: MlrUnverifiedClaim;
    }
  | {
      type: "records";
      sections: Record<string, unknown>;
      fallbackText?: string | null;
      editable?: MlrEditableTarget;
    }
  | {
      type: "declaration";
      /** Where this section sits in the report's shape (12 on an RTA report). */
      sectionNumber?: number;
      /**
       * Whether §x.1 — the whiplash-tariff triage — belongs to this report.
       * False on shapes where the tariff cannot apply, which also moves every
       * subsection below it up by one. (19-09-2026)
       */
      includeClassification?: boolean;
      doctorName?: string;
      qualifications?: string | null;
      attestation?: {
        confirmed: true;
        signerName?: string | null;
        signedAt?: string | null;
      } | null;
      /**
       * The sentences being signed, from the same source the PDF prints.
       * Never re-worded here: a Sign button beside a paraphrase asks for a
       * signature on words the panel made up.
       */
      statementOfTruth?: string[];
      /**
       * §12.1's three answers, each with where it came from.
       *
       * ⚠️ `value: null` means **nobody has answered**, and the report prints a
       * blank rule for it. All three used to default to "Yes" on both sides —
       * so a report nobody had touched asserted three things about the whiplash
       * tariff above a doctor's signature. (18-09-2026)
       */
      caseClassification?: Record<string, MlrClassificationAnswer>;
    };

/** One §12.1 answer, and what settled it. */
export type MlrClassificationAnswer = {
  value: string | null;
  /** `doctor` outranks everything; `null` means nothing decided it. */
  source: "doctor" | "rulebook" | "intake" | null;
  cite?: string | null;
  because?: string[];
  question?: string;
};

/** §7 only — what an editor may start that the printed report has no block for. */
export type MlrEditorSlot =
  | {
      kind: "examination";
      field: "generalFindings" | "presentationFindings";
      label: string;
      html: string;
    }
  | { kind: "examinationInjury"; injuryKey: string; label: string; html: string };

/** §6 — which records the doctor reviewed, said and attested by them. */
export type MlrRecordSourceKey =
  | "gp"
  | "ambulance"
  | "aande"
  | "hospital"
  | "radiology"
  | "physiotherapy"
  | "occupationalHealth"
  | "priorMedicolegal"
  | "instructions"
  | "compensator";

export type MlrRecordsStatus = "reviewed" | "none_received" | "awaited";

export type MlrRecordsReview = {
  status: MlrRecordsStatus | null;
  sources: Array<{ key: MlrRecordSourceKey; dateLabel?: string }>;
  legibleAndComplete: boolean | null;
  /** The doctor's own words, when it came from a dictated clip. */
  quote?: string;
  note?: string;
  confirmed: boolean;
};

export type MlrReportSection = {
  number: number;
  id: string;
  title: string;
  status: z.infer<typeof MlrSectionStatusSchema>;
  blocks: MlrBlock[];
  editorSlots?: MlrEditorSlot[];
  /**
   * §6 only. The printed blocks carry only what is CONFIRMED, so the panel
   * needs the answer itself — draft included. Null on a frozen report.
   */
  recordsReview?: MlrRecordsReview | null;
  /**
   * §9 only. The three exceptional-severity answers as data, so the panel can
   * offer Yes/No controls without keeping a second copy of the questions.
   */
  exceptionalSeverity?: {
    /** The doctor's free text has replaced the questions entirely. */
    overridden: boolean;
    questions: Array<{
      /** The supplement field this answer saves to. */
      key: string;
      question: string;
      /**
       * What the report prints — the stored answer, or the derivation.
       * `null` means genuinely unanswered: the PDF prints a blank rule and the
       * panel offers the buttons.
       */
      value: string | null;
      /** The doctor's own answer, or null when this was derived. */
      answered: string | null;
    }>;
  } | null;
};

export type MlrReportSections = {
  assessmentId?: string | null;
  claimantName?: string | null;
  doctorName?: string | null;
  qualifications?: string | null;
  reportDate?: string | null;
  statementOfTruthSigned?: boolean;
  confirmedPrognosisCount?: number;
  snapshot?: { version: number; signerName?: string | null; signedAt?: string | null } | null;
  sections: MlrReportSection[];
};

// ----------------------------------------------------------------------
// Report instructions — one box per MLR section, read before every
// generation. Versioned rather than mutable: a signed MLR has to be able to
// say what the model was told when it was written. Super admin only.
// (14-09-2026)
// ----------------------------------------------------------------------

export const MlrInstructionSetSchema = z
  .object({
    id: z.number(),
    section: z.string(),
    version: z.number(),
    body: z.string(),
    note: z.string().nullable().optional(),
    createdBy: z.string().nullable().optional(),
    createdAt: z.union([z.string(), z.date()]).nullable().optional(),
  })
  .passthrough();

/** The editable sections, named by the backend so the panel keeps no copy. */
export const MlrInstructionSectionSchema = z.object({
  key: z.string(),
  label: z.string(),
  /** The report's own heading number ("3.1", "5.2.2"); absent for `general`. */
  number: z.string().optional(),
  hint: z.string().optional(),
});

export const MlrInstructionsResponseSchema = z.object({
  success: z.boolean().optional(),
  sections: z.array(MlrInstructionSectionSchema).default([]),
  instructions: z.record(MlrInstructionSetSchema.nullable()).default({}),
  versions: z.array(MlrInstructionSetSchema).default([]),
});

export const MlrInstructionsSaveResponseSchema = z.object({
  success: z.boolean().optional(),
  instructions: MlrInstructionSetSchema,
});

export type MlrInstructionSet = z.infer<typeof MlrInstructionSetSchema>;
export type MlrInstructionSection = z.infer<typeof MlrInstructionSectionSchema>;


// ----------------------------------------------------------------------
// The rulebook — what decides what goes in a report (Phase E)
// ----------------------------------------------------------------------

/** `[factPath, operator, value?]` — see BACKEND rulebook/rule.schema.mjs. */
export const RuleConditionSchema = z.tuple([
  z.string(),
  z.string(),
  z.unknown().optional(),
]);

export const MlrRuleSchema = z.object({
  id: z.string(),
  decision: z.string(),
  when: z.array(RuleConditionSchema),
  then: z.string(),
  /** Fires only on a KNOWN false — never on a missing fact. */
  otherwise: z.string().optional(),
  cite: z.string(),
});

export type MlrRule = z.infer<typeof MlrRuleSchema>;

export const MlrRulebookResponseSchema = z
  .object({
    success: z.boolean().optional(),
    rulebook: z.object({
      rules: z.array(MlrRuleSchema),
      /** Null while the code rules are in force — nothing published yet. */
      version: z.number().nullable(),
      source: z.enum(["published", "code"]),
    }),
    versions: z.array(
      z.object({
        version: z.number(),
        note: z.string().nullable().optional(),
        createdAt: z.union([z.string(), z.date()]).nullable().optional(),
      }),
    ),
    /** The vocabulary a rule may be written in — served, never shipped. */
    factPaths: z.array(z.string()),
    decisions: z.record(z.array(z.string())),
    operators: z.array(z.string()),
  })
  .passthrough();

export type MlrRulebookPayload = z.infer<typeof MlrRulebookResponseSchema>;

export const MlrRulebookPreviewSchema = z
  .object({
    success: z.boolean().optional(),
    preview: z.object({
      problems: z.array(z.string()),
      sampled: z.number(),
      injuries: z.number(),
      liveVersion: z.number().nullable().optional(),
      summary: z.record(
        z.object({
          applies: z.number(),
          notApplicable: z.number(),
          undetermined: z.number(),
        }),
      ),
      changed: z.array(
        z.object({
          assessmentId: z.string(),
          claimant: z.string(),
          injury: z.string(),
          decision: z.string(),
          was: z.string().nullable(),
          now: z.string().nullable(),
          wasRule: z.string().nullable(),
          nowRule: z.string().nullable(),
        }),
      ),
    }),
  })
  .passthrough();

export type MlrRulebookPreview = z.infer<
  typeof MlrRulebookPreviewSchema
>["preview"];
